import { format, startOfMonth, startOfYear, subDays } from 'date-fns';

import { formatarData } from '@/lib/utils';
import { ROTULOS_PRIORIDADE, ROTULOS_TIPO } from '@/features/solicitacoes/status';
import { ROTULOS_ORIGEM } from '@/features/solicitacoes/origem';

/**
 * Filtros da tela de relatórios.
 *
 * Todos vivem na URL: um relatório é feito para ser conferido por outra pessoa,
 * e um link que abre exatamente o mesmo recorte vale mais do que qualquer
 * explicação por escrito de como chegar até ele.
 */
export interface FiltrosRelatorio {
  de: string;
  ate: string;
  ubsId: string;
  especialidadeId: string;
  tipo: string;
  prioridade: string;
  origem: string;
  limite: string;
}

/** Dias do recorte padrão, contando hoje. */
const DIAS_PADRAO = 90;

export const LIMITES = ['5', '10', '20', '50'] as const;

/** Data no fuso do usuário — nunca em UTC, ou o relatório abriria em ontem. */
const iso = (data: Date): string => format(data, 'yyyy-MM-dd');

export interface Preset {
  chave: string;
  rotulo: string;
  calcular: (agora: Date) => { de: string; ate: string };
}

/**
 * Atalhos de período. "Últimos N dias" inclui hoje: quem clica em "7 dias" na
 * quinta-feira espera ver a quinta-feira.
 */
const NOVENTA_DIAS: Preset = {
  chave: '90d',
  rotulo: '90 dias',
  calcular: (agora) => ({ de: iso(subDays(agora, DIAS_PADRAO - 1)), ate: iso(agora) }),
};

export const PRESETS: Preset[] = [
  {
    chave: '7d',
    rotulo: '7 dias',
    calcular: (agora) => ({ de: iso(subDays(agora, 6)), ate: iso(agora) }),
  },
  {
    chave: '30d',
    rotulo: '30 dias',
    calcular: (agora) => ({ de: iso(subDays(agora, 29)), ate: iso(agora) }),
  },
  NOVENTA_DIAS,
  {
    chave: 'mes',
    rotulo: 'Este mês',
    calcular: (agora) => ({ de: iso(startOfMonth(agora)), ate: iso(agora) }),
  },
  {
    chave: 'ano',
    rotulo: 'Este ano',
    calcular: (agora) => ({ de: iso(startOfYear(agora)), ate: iso(agora) }),
  },
];

/** Período padrão, calculado no cliente para que a URL seja sempre explícita. */
export function periodoPadrao(agora = new Date()): { de: string; ate: string } {
  return NOVENTA_DIAS.calcular(agora);
}

export function lerFiltros(parametros: URLSearchParams, agora = new Date()): FiltrosRelatorio {
  const padrao = periodoPadrao(agora);

  return {
    de: parametros.get('de') ?? padrao.de,
    ate: parametros.get('ate') ?? padrao.ate,
    ubsId: parametros.get('ubs') ?? '',
    especialidadeId: parametros.get('especialidade') ?? '',
    tipo: parametros.get('tipo') ?? '',
    prioridade: parametros.get('prioridade') ?? '',
    origem: parametros.get('origem') ?? '',
    limite: parametros.get('limite') ?? '10',
  };
}

/**
 * Monta a consulta enviada ao backend.
 *
 * O período vai sempre explícito, mesmo quando é o padrão: assim a tela e o CSV
 * baixado ao lado dela falam do mesmo recorte, sem depender de os dois
 * calcularem "90 dias" no mesmo instante.
 */
export function paraConsulta(filtros: FiltrosRelatorio): URLSearchParams {
  const consulta = new URLSearchParams({
    de: filtros.de,
    ate: filtros.ate,
    limite: filtros.limite,
  });

  if (filtros.ubsId) consulta.set('ubsId', filtros.ubsId);
  if (filtros.especialidadeId) consulta.set('especialidadeId', filtros.especialidadeId);
  if (filtros.tipo) consulta.set('tipo', filtros.tipo);
  if (filtros.prioridade) consulta.set('prioridade', filtros.prioridade);
  if (filtros.origem) consulta.set('origem', filtros.origem);

  return consulta;
}

/**
 * A data digitada já pode virar filtro?
 *
 * O `<input type="date">` entrega a data a cada segmento preenchido, e o ano
 * vem dígito a dígito: quem digita "2026" passa por 0002, 0020 e 0202, todas
 * datas válidas para o navegador. Mandar cada uma para a URL dispararia uma
 * consulta por tecla sobre um ano que ninguém pediu. Antes de 1900 não há
 * solicitação possível, então o corte separa o meio da digitação do fim dela.
 */
export function dataDigitadaValida(valor: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false;

  const [ano, mes, dia] = valor.split('-').map(Number) as [number, number, number];
  if (ano < 1900) return false;

  // 31/02 passa na expressão; a data reconstruída é que denuncia.
  const data = new Date(Date.UTC(ano, mes - 1, dia));
  return data.getUTCMonth() === mes - 1 && data.getUTCDate() === dia;
}

/** Qual atalho corresponde ao período atual, se algum. */
export function presetAtivo(filtros: FiltrosRelatorio, agora = new Date()): string | null {
  const encontrado = PRESETS.find((preset) => {
    const intervalo = preset.calcular(agora);
    return intervalo.de === filtros.de && intervalo.ate === filtros.ate;
  });

  return encontrado?.chave ?? null;
}

/** Há algo além do período padrão e do limite padrão? */
export function temFiltroAlemDoPeriodo(filtros: FiltrosRelatorio): boolean {
  return Boolean(
    filtros.ubsId ||
    filtros.especialidadeId ||
    filtros.tipo ||
    filtros.prioridade ||
    filtros.origem,
  );
}

/**
 * Filtros em texto, para o cabeçalho da versão impressa.
 *
 * Um relatório impresso circula solto: sem dizer de que recorte fala, a folha
 * vira um punhado de números sem procedência.
 */
export function descreverFiltros(
  filtros: FiltrosRelatorio,
  nomes: { ubs?: string; especialidade?: string },
): string[] {
  const partes = [`Período: ${formatarData(filtros.de)} a ${formatarData(filtros.ate)}`];

  partes.push(`UBS: ${filtros.ubsId ? (nomes.ubs ?? 'unidade selecionada') : 'todas do escopo'}`);

  if (filtros.especialidadeId) {
    partes.push(`Especialidade: ${nomes.especialidade ?? 'selecionada'}`);
  }
  if (filtros.tipo) {
    partes.push(`Tipo: ${ROTULOS_TIPO[filtros.tipo as keyof typeof ROTULOS_TIPO] ?? filtros.tipo}`);
  }
  if (filtros.prioridade) {
    const rotulo = ROTULOS_PRIORIDADE[filtros.prioridade as keyof typeof ROTULOS_PRIORIDADE];
    partes.push(`Prioridade: ${rotulo ?? filtros.prioridade}`);
  }
  // Só quando filtrada, como especialidade, tipo e prioridade: dizer "todas as
  // origens" em toda folha impressa seria ruído num relatório que, na imensa
  // maioria das vezes, soma as duas.
  if (filtros.origem) {
    const rotulo = ROTULOS_ORIGEM[filtros.origem as keyof typeof ROTULOS_ORIGEM];
    partes.push(`Origem: ${rotulo ?? filtros.origem}`);
  }

  return partes;
}
